import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, DirectionsRenderer, Marker, useJsApiLoader } from '@react-google-maps/api';
import { MapPin, Route, Search } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ensureUserAccount } from '@/services/accountService';
import { getClients } from '@/services/clientService';
import AppShell, { PageLoading } from '@/components/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

const GOOGLE_MAPS_LIBRARIES = ['places', 'drawing', 'geometry'];
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const MAX_OPTIMIZED_STOPS = 25;

const mapContainerStyle = {
  width: '100%',
  height: '560px',
  borderRadius: '8px',
};

function getClientLabel(client) {
  return client.customer_name || client.property_address || 'Unnamed client';
}

function formatDistance(route) {
  const meters = route?.legs?.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0) || 0;
  const miles = meters / 1609.344;
  return miles ? `${miles.toFixed(1)} mi` : 'N/A';
}

function formatDuration(route) {
  const seconds = route?.legs?.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0) || 0;
  if (!seconds) return 'N/A';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (!hours) return `${minutes} min`;
  return `${hours} hr ${minutes} min`;
}

function getOptimizedClients(selectedClients, waypointOrder = []) {
  if (!waypointOrder.length) return selectedClients;
  return waypointOrder.map((index) => selectedClients[index]).filter(Boolean);
}

function getDirectionsStatusMessage(status) {
  const statusMessages = {
    INVALID_REQUEST: 'The route request was missing required map data.',
    MAX_WAYPOINTS_EXCEEDED: 'Too many stops were selected for one Google Maps route.',
    NOT_FOUND: 'Google could not find one of the selected addresses.',
    OVER_QUERY_LIMIT: 'Google Maps rate limits were reached. Wait a moment and try again.',
    REQUEST_DENIED: 'Google rejected the route request. Check that the Directions API is enabled for your Google Maps key.',
    UNKNOWN_ERROR: 'Google Maps had a temporary routing error. Try again in a moment.',
    ZERO_RESULTS: 'Google could not find a drivable route through these stops.',
  };

  return statusMessages[status] || 'Google Maps could not create a route for these stops.';
}

function toLatLngLiteral(location) {
  return {
    lat: location.lat,
    lng: location.lng,
  };
}

export default function RouteOptimization() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [account, setAccount] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [startAddress, setStartAddress] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientLocations, setClientLocations] = useState({});
  const [startLocation, setStartLocation] = useState(null);
  const [directions, setDirections] = useState(null);
  const [routeSummary, setRouteSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const isMapsConfigured = !!googleMapsApiKey;

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const result = await ensureUserAccount(user);
      const accountId = result?.account?.id;
      setAccount(result?.account || null);

      if (!accountId) {
        setClients([]);
        return;
      }

      const clientList = await getClients(accountId);
      setClients(clientList.filter((client) => client.property_address));
    } catch (err) {
      console.error('[RouteOptimization] Error loading data:', err);
      setError(err.message || 'Failed to load route planning data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadData();
    } else if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, loadData, navigate, user]);

  const filteredClients = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return clients;

    return clients.filter((client) => {
      return [
        client.customer_name,
        client.property_address,
        client.customer_email,
        client.customer_phone,
      ].filter(Boolean).join(' ').toLowerCase().includes(search);
    });
  }, [clients, searchTerm]);

  const selectedClients = useMemo(() => {
    return clients.filter((client) => selectedClientIds.includes(client.id));
  }, [clients, selectedClientIds]);

  const routeClients = useMemo(() => {
    return getOptimizedClients(selectedClients, routeSummary?.waypointOrder);
  }, [routeSummary, selectedClients]);

  const mapCenter = startLocation || Object.values(clientLocations)[0] || DEFAULT_CENTER;

  const toggleClient = (clientId) => {
    setDirections(null);
    setRouteSummary(null);
    setSelectedClientIds((prev) => (
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    ));
  };

  const selectVisibleClients = () => {
    setDirections(null);
    setRouteSummary(null);
    setSelectedClientIds(filteredClients.slice(0, MAX_OPTIMIZED_STOPS).map((client) => client.id));
  };

  const clearRoute = () => {
    setSelectedClientIds([]);
    setClientLocations({});
    setStartLocation(null);
    setDirections(null);
    setRouteSummary(null);
  };

  const geocodeAddress = (geocoder, address, label = address) => {
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            formattedAddress: results[0].formatted_address,
          });
        } else {
          reject(new Error(`Could not map "${label}". Google returned ${status}. Check the saved address and try again.`));
        }
      });
    });
  };

  const planRoute = async () => {
    if (!isLoaded || !window.google?.maps) {
      setError('Google Maps is still loading. Try again in a moment.');
      return;
    }

    if (!startAddress.trim()) {
      setError('Enter a starting address, such as your shop, yard, or first stop.');
      return;
    }

    if (selectedClients.length < 1) {
      setError('Select at least one client stop to optimize.');
      return;
    }

    if (selectedClients.length > MAX_OPTIMIZED_STOPS) {
      setError(`Select ${MAX_OPTIMIZED_STOPS} or fewer stops for one optimized route.`);
      return;
    }

    try {
      setPlanning(true);
      setError(null);
      setDirections(null);
      setRouteSummary(null);

      const geocoder = new window.google.maps.Geocoder();
      const directionsService = new window.google.maps.DirectionsService();
      const origin = await geocodeAddress(geocoder, startAddress, 'start / end location');
      const locations = [];

      for (const client of selectedClients) {
        const location = await geocodeAddress(geocoder, client.property_address, getClientLabel(client));
        locations.push(location);
      }

      const nextClientLocations = {};
      selectedClients.forEach((client, index) => {
        nextClientLocations[client.id] = locations[index];
      });

      const result = await new Promise((resolve, reject) => {
        directionsService.route(
          {
            origin: toLatLngLiteral(origin),
            destination: toLatLngLiteral(origin),
            waypoints: locations.map((location) => ({ location: toLatLngLiteral(location), stopover: true })),
            optimizeWaypoints: true,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (response, status) => {
            if (status === 'OK' && response) {
              resolve(response);
            } else {
              reject(new Error(`${getDirectionsStatusMessage(status)} Google returned ${status}. Try removing one or two stops, verify the addresses, then optimize again.`));
            }
          }
        );
      });

      const primaryRoute = result.routes?.[0];
      setStartLocation(origin);
      setClientLocations(nextClientLocations);
      setDirections(result);
      setRouteSummary({
        distance: formatDistance(primaryRoute),
        duration: formatDuration(primaryRoute),
        waypointOrder: primaryRoute?.waypoint_order || [],
      });
    } catch (err) {
      console.error('[RouteOptimization] Error planning route:', err);
      setError(err.message || 'Failed to optimize route');
    } finally {
      setPlanning(false);
    }
  };

  if (authLoading || loading) {
    return <PageLoading label="Loading routes..." />;
  }

  return (
    <AppShell
      title="Route Optimization"
      description="Map client stops and let GreenQuote plan an efficient driving route."
      accountName={account?.name}
      userLabel={user?.user_metadata?.full_name || user?.email}
      maxWidth="max-w-8xl"
    >
      <div className="space-y-6">
        {(error || loadError || !isMapsConfigured) && (
          <Alert variant={error ? 'destructive' : 'default'}>
            <AlertDescription>
              {error || loadError?.message || 'Google Maps is not configured for this environment.'}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_1fr]">
          <div className="space-y-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Build Route</CardTitle>
                <CardDescription>Select customers, add a starting point, then optimize the route.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label htmlFor="startAddress" className="text-sm font-medium text-slate-700">
                    Start / End Location
                  </label>
                  <Input
                    id="startAddress"
                    className="mt-1"
                    value={startAddress}
                    onChange={(event) => setStartAddress(event.target.value)}
                    placeholder="Shop, yard, or home base address"
                  />
                  <p className="mt-1 text-xs text-slate-500">Routes return to this same location.</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md bg-slate-50 p-3">
                    <p className="text-xs text-slate-500">Selected Stops</p>
                    <p className="text-2xl font-semibold text-slate-950">{selectedClients.length}</p>
                  </div>
                  <div className="rounded-md bg-slate-50 p-3">
                    <p className="text-xs text-slate-500">Route Limit</p>
                    <p className="text-2xl font-semibold text-slate-950">{MAX_OPTIMIZED_STOPS}</p>
                  </div>
                </div>

                {routeSummary && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-md bg-emerald-50 p-3">
                      <p className="text-xs text-emerald-700">Distance</p>
                      <p className="text-lg font-semibold text-emerald-950">{routeSummary.distance}</p>
                    </div>
                    <div className="rounded-md bg-emerald-50 p-3">
                      <p className="text-xs text-emerald-700">Drive Time</p>
                      <p className="text-lg font-semibold text-emerald-950">{routeSummary.duration}</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    className="bg-emerald-700 hover:bg-emerald-800"
                    onClick={planRoute}
                    disabled={planning || !isMapsConfigured || !isLoaded}
                  >
                    <Route className="mr-2 h-4 w-4" />
                    {planning ? 'Optimizing...' : 'Optimize Route'}
                  </Button>
                  <Button type="button" variant="outline" onClick={clearRoute}>
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>Client Stops</CardTitle>
                    <CardDescription>{filteredClients.length} mappable client{filteredClients.length !== 1 ? 's' : ''}</CardDescription>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={selectVisibleClients}>
                    Select Visible
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative mb-3">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="search"
                    className="pl-9"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search clients or addresses..."
                  />
                </div>

                <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                  {filteredClients.map((client) => {
                    const selected = selectedClientIds.includes(client.id);
                    return (
                      <label
                        key={client.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                          selected ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <Checkbox checked={selected} onCheckedChange={() => toggleClient(client.id)} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-slate-950">{getClientLabel(client)}</span>
                          <span className="mt-1 flex items-start gap-1.5 text-xs text-slate-500">
                            <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                            <span>{client.property_address}</span>
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-3">
                {isLoaded && isMapsConfigured ? (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={startLocation || Object.keys(clientLocations).length ? 11 : 4}
                    options={{
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: true,
                    }}
                  >
                    {directions ? (
                      <DirectionsRenderer directions={directions} />
                    ) : (
                      <>
                        {startLocation && (
                          <Marker position={startLocation} label="Start" />
                        )}
                        {Object.entries(clientLocations).map(([clientId, location], index) => (
                          <Marker key={clientId} position={location} label={`${index + 1}`} />
                        ))}
                      </>
                    )}
                  </GoogleMap>
                ) : (
                  <div className="flex h-[560px] items-center justify-center rounded-md bg-slate-100 text-sm text-slate-500">
                    Google Maps is required to use route optimization.
                  </div>
                )}
              </CardContent>
            </Card>

            {routeClients.length > 0 && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle>Optimized Stop Order</CardTitle>
                  <CardDescription>Follow this order after leaving your start location.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {routeClients.map((client, index) => (
                      <div key={client.id} className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-start gap-3">
                          <Badge className="bg-emerald-700">{index + 1}</Badge>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-950">{getClientLabel(client)}</p>
                            <p className="mt-1 text-xs text-slate-500">{client.property_address}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
